import type { ChatMessages, Features, Icons, Scope } from "@storytell/cp-client";
import type { PmDoc } from "@storytell/types/richText";
import type { SetStoreFunction } from "solid-js/store";
import type { AggregatedMetrics } from "../types";
import type { UserId } from "@storytell/types";

// Streaming statuses used to track each message's status
// The status of the chat is computed based on the status of all messages in the chat
// We can't rely on the last message in case the user interrupts the chat and there's
// 2 parallel operations running.
export enum StreamingMessageStatus {
  IDLE = "IDLE",
  GENERATING = "GENERATING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}
// The chat is either idle or in progress
// We don't need to track if it's completed or failed, that's handled per message
export enum StreamingChatStatus {
  IDLE = "IDLE",
  GENERATING = "GENERATING",
}

// ------------------------------------------------------------
// Store
// ------------------------------------------------------------
// Main state for managing chats
export type StreamingStore = {
  chats: StreamingChatsRecord;
  messages: StreamingMessagesRecord;
  operations: StreamingOperationsRecord;
  blocks: StreamingBlocksRecord;
};
// Setter
export type SetStreamingStore = SetStoreFunction<StreamingStore>;

// ------------------------------------------------------------
// Typesafe IDs
// ------------------------------------------------------------
export type StreamingMessageId = `message_${string}`;
export type StreamingChatId = `chat_${string}`;
export type StreamingOperationId = `operation_${string}`;
export type StreamingBlockId = `block_${string}`;

// ------------------------------------------------------------
// Records
// ------------------------------------------------------------
export type StreamingChatsRecord = Record<StreamingChatId, StreamingChat | undefined>;
export type StreamingOperationsRecord = Record<StreamingOperationId, StreamingOperation | undefined>;
export type StreamingBlocksRecord = Record<StreamingBlockId, StreamingBlock | undefined>;
export type StreamingMessagesRecord = Record<StreamingMessageId, StreamingMessage | undefined>;

// ------------------------------------------------------------
// Chat
// ------------------------------------------------------------
//
/**
 * Hierarchy of data:
 * ```
 * Chat
 * │ Messages
 * │ │ Prompt
 * │ │ Operations (each operation can have a task property)
 * │ │ │ Blocks
 * ```
 */
export type StreamingChat = {
  id: StreamingChatId;
  messages: StreamingMessageId[];
  aggregateMetrics: AggregatedMetrics;
};

// ------------------------------------------------------------
// Message
// ------------------------------------------------------------
// A message is a series of operations made up of blocks
// Each opereation roughly maps to a section of the UI, e.g.: the submitted prompt, a knowledge base search, etc.
export type StreamingMessage = {
  id: StreamingMessageId;
  chatId: StreamingChatId;
  feature: Features.Feature;
  index: number;
  role: ChatMessages.Role;
  prompt: StreamingPrompt;
  operations: StreamingOperationId[];
  status: StreamingMessageStatus;
  aggregateMetrics: AggregatedMetrics;
  createdBy: UserId;
};

// ------------------------------------------------------------
// Prompt
// ------------------------------------------------------------
export type StreamingPrompt = {
  messageId: StreamingMessageId;
  blockId: StreamingBlockId;
  operationId: StreamingOperationId;
  text: string;
  richText?: PmDoc;
  scope: Scope.Scope | undefined;
};

// ------------------------------------------------------------
// Operation
// ------------------------------------------------------------
export type StreamingOperation = {
  index: number;
  isComplete: boolean;
  operationId: StreamingOperationId;
  messageId: StreamingMessageId;
  blocks: StreamingBlockId[];
  name: string;
  activity: string;
  startedAt: Date | undefined;
  role: ChatMessages.Role;
  status: ChatMessages.OperationStatus;
  completedAt: Date | undefined;
  task: StreamingTask | undefined;
  progressMessage: string | undefined;
};

// ------------------------------------------------------------
// Block
// ------------------------------------------------------------
export type StreamingBlockMetrics = {
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;
  ingressCredits?: number;
  egressCredits?: number;
};

// Structured search result types
export type StructuredSearchResult = {
  assetId: string;
  assetName: string;
  resultCount?: number;
  markdownTable?: string;
  // Raw data from backend
  headers?: string[];
  rows?: string[][];
  queryIndex?: number;
  totalQueries?: number;
  rowCount?: number;
  // Completion message fields
  isCompletion?: boolean;
  duration?: number;
  assetCount?: number;
  // Explains why this SQL query was chosen
  queryReason?: string;
  // The natural language query that generated these results
  query?: string;
};

export type FinalContent =
  | { type: "text"; text: string }
  | { type: "richText"; richText: PmDoc }
  | { type: "retry"; message: string }
  | { type: "error"; displayMessage: string; systemDetail?: string; reason?: string };

export type StreamingBlock = {
  type: "block";
  isComplete: boolean;
  blockId: StreamingBlockId;
  operationId: StreamingOperationId;
  messageId: StreamingMessageId;
  role: ChatMessages.Role;
  activity: string | undefined;
  feature: Features.Feature;
  messageType: ChatMessages.MessageType;
  title: string | undefined;
  deltas: { text: string }[];
  finalContent: FinalContent | undefined;
  searchResults: ChatMessages.SearchResult | undefined;
  webSearchResults: ChatMessages.WebSearchResult | undefined;
  imageResults: ChatMessages.ImageResult | undefined;
  conceptResults: ChatMessages.ConceptResult | undefined;
  structuredResults: StructuredSearchResult | undefined;
  metrics: StreamingBlockMetrics | undefined;
};

// ------------------------------------------------------------
// Task
// ------------------------------------------------------------
export type StreamingTask = {
  type: "task";
  isComplete: boolean;
  blockId: StreamingBlockId;
  operationId: StreamingOperationId;
  messageId: StreamingMessageId;
  role: ChatMessages.Role;
  taskId: string;
  taskName: string;
  status: ChatMessages.TaskStatus;
  progressPercent: number;
  currentActivity: string | undefined;
  dependencies: string[];
  icon: Icons.Icon;
  durationInfo: ChatMessages.TaskDurationInfo | undefined;
  errorMessage: string | undefined;
};
